import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  MapPin,
  Building2,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  quote: 'bg-gray-50 text-gray-700 border-gray-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  on_hold: 'bg-orange-50 text-orange-700 border-orange-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-700 border-slate-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600'
};

const Jobs = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [jobs, setJobs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('all');

  const statusLabels = {
    quote: t('jobManagement.jobStatus.quote'),
    approved: t('jobManagement.jobStatus.approved'),
    in_progress: t('jobManagement.jobStatus.inProgress'),
    on_hold: t('jobManagement.jobStatus.onHold'),
    completed: t('jobManagement.jobStatus.completed'),
    closed: t('jobManagement.jobStatus.closed'),
    cancelled: t('jobManagement.jobStatus.cancelled')
  };

  const priorityLabels = {
    low: t('pages.projects.low'),
    medium: t('pages.projects.medium'),
    high: t('pages.projects.high'),
    urgent: t('pages.projects.urgent')
  };

  useEffect(() => {
    fetchJobs();
    fetchSites();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs`);
      setJobs(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/sites`);
      setSites(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/jobs`, formData);
      toast.success(t('jobManagement.jobCreated'));
      setShowDialog(false);
      setFormData({});
      fetchJobs();
      fetchSites();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : t('jobManagement.failedToSave'));
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('jobManagement.confirmDeleteJob'))) {
      try {
        await axios.delete(`${API_URL}/api/jobs/${id}`);
        toast.success(t('jobManagement.jobDeleted'));
        fetchJobs();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('jobManagement.failedToDelete'));
      }
    }
  };

  const openNew = () => {
    setFormData({ priority: 'medium' });
    setShowDialog(true);
  };

  const filteredJobs = jobs
    .filter(j => filterStatus === 'all' || j.status === filterStatus)
    .filter(j => filterSite === 'all' || j.site_id === filterSite)
    .filter(j => !search || j.title?.toLowerCase().includes(search.toLowerCase()) || j.job_number?.includes(search));

  const stats = {
    total: jobs.length,
    inProgress: jobs.filter(j => j.status === 'in_progress').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    totalValue: jobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0),
    totalPaid: jobs.reduce((sum, j) => sum + (j.total_paid || 0), 0),
    outstanding: jobs.reduce((sum, j) => sum + (j.balance_due || 0), 0)
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('jobManagement.jobs')}</h1>
          <p className="text-muted-foreground">{t('jobManagement.jobsSubtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('jobManagement.newJob')}
        </Button>
      </div>

      <div className="grid grid-cols-6 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">{t('jobManagement.totalJobs')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{stats.inProgress}</div><div className="text-sm text-muted-foreground">{t('jobManagement.jobStatus.inProgress')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{stats.completed}</div><div className="text-sm text-muted-foreground">{t('jobManagement.jobStatus.completed')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{currencySymbol} {stats.totalValue.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('jobManagement.totalValue')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{currencySymbol} {stats.totalPaid.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('jobManagement.totalPaid')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{currencySymbol} {stats.outstanding.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('jobManagement.outstanding')}</div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
          <Input className={isRTL ? 'pr-10' : 'pl-10'} placeholder={t('jobManagement.searchJobs')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterSite} onValueChange={setFilterSite}>
          <SelectTrigger className="w-48"><SelectValue placeholder={t('jobManagement.filterBySite')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('jobManagement.allSites')}</SelectItem>
            {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allStatus')}</SelectItem>
            {Object.entries(statusLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('jobManagement.jobNumber')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('jobManagement.projectName')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('jobManagement.jobTitle')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('jobManagement.scopes')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('jobManagement.progress')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('jobManagement.value')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.clients.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredJobs.map((job) => (
              <TableRow key={job.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/projects/${job.id}`)}>
                <TableCell className="font-mono font-medium">{job.job_number}</TableCell>
                <TableCell>
                  <div className={`flex items-center gap-1 text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Briefcase className="h-3 w-3 text-muted-foreground" />
                    {job.project_name || job.title}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{job.title}</div>
                  <div className="text-xs text-muted-foreground">{job.client_name || '-'}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{job.scopes?.length || 0} {t('jobManagement.scopes')}</Badge>
                </TableCell>
                <TableCell>
                  <div className="w-24">
                    <div className={`flex items-center justify-between text-xs mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span>{job.overall_progress || 0}%</span>
                    </div>
                    <Progress value={job.overall_progress || 0} className="h-2" />
                  </div>
                </TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono`}>
                  <div>{currencySymbol} {(job.estimated_value || 0).toLocaleString()}</div>
                  {job.balance_due > 0 && (
                    <div className="text-xs text-red-600">{t('jobManagement.due')}: {currencySymbol} {job.balance_due.toLocaleString()}</div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Badge className={statusColors[job.status]}>{statusLabels[job.status]}</Badge>
                    <Badge className={`${priorityColors[job.priority]} text-xs`}>{priorityLabels[job.priority]}</Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/${job.id}`); }}>
                        <Eye className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('jobManagement.viewDetails')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(job.id); }}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <Briefcase className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('jobManagement.noJobs')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('jobManagement.newJob')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('jobManagement.projectName')} *</Label>
              <Input 
                value={formData.project_name || ''} 
                onChange={e => setFormData({...formData, project_name: e.target.value})} 
                placeholder={isRTL ? "أدخل اسم المشروع" : "Enter project name"} 
              />
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.jobTitle')} *</Label>
              <Input value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Network Infrastructure Setup" />
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.description')}</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.priority')}</Label>
                <Select value={formData.priority || 'medium'} onValueChange={v => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('pages.projects.low')}</SelectItem>
                    <SelectItem value="medium">{t('pages.projects.medium')}</SelectItem>
                    <SelectItem value="high">{t('pages.projects.high')}</SelectItem>
                    <SelectItem value="urgent">{t('pages.projects.urgent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.estimatedValue')}</Label>
                <Input type="number" value={formData.estimated_value || ''} onChange={e => setFormData({...formData, estimated_value: parseFloat(e.target.value) || 0})} placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.startDate')}</Label>
                <Input type="date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.expectedEndDate')}</Label>
                <Input type="date" value={formData.expected_end_date || ''} onChange={e => setFormData({...formData, expected_end_date: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Jobs;
